import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { BsArrowLeft, BsHeadset, BsPaperclip, BsDownload } from 'react-icons/bs';
import { useCustomer } from './CustomerContext';
import StatusBadge from './components/StatusBadge';
import EmptyState from './components/EmptyState';
import { apiFetch, parseJsonSafe, formatDate } from '../../utils/api';

const fmtDateTime = (v) => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString('es-ES');
};

const backLink = (
  <Link to="/cliente/soporte" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: 'var(--cp-accent)', textDecoration: 'none', fontWeight: 500, marginBottom: 14 }}>
    <BsArrowLeft /> Soporte
  </Link>
);

const CustomerSupportDetailPage = () => {
  const { id } = useParams();
  const { authHeaders, logout } = useCustomer();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reply, setReply] = useState('');
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch(`/api/v1/software/customer/portal/tickets/${id}`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return; }
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error((data && data.message) || 'No se pudo cargar el ticket');
      setTicket(data);
    } catch (e) {
      setError(e.message);
      setTicket(null);
    } finally {
      setLoading(false);
    }
  }, [id, authHeaders, logout]);

  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!reply.trim() && !files.length) return;
    setSending(true);
    setError('');
    try {
      let messageId = null;
      if (reply.trim()) {
        const res = await apiFetch(`/api/v1/software/customer/portal/tickets/${id}/messages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify({ body: reply.trim() })
        });
        if (res.status === 401) { logout(); return; }
        const data = await parseJsonSafe(res);
        if (!res.ok) throw new Error((data && data.message) || 'No se pudo enviar');
        messageId = data.id;
      }
      const failed = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        if (messageId) fd.append('message_id', messageId);
        const up = await apiFetch(`/api/v1/software/customer/portal/tickets/${id}/attachments`, { method: 'POST', headers: authHeaders(), body: fd });
        if (up.status === 401) { logout(); return; }
        if (!up.ok) failed.push(file.name);
      }
      if (failed.length) {
        setError(`No se pudieron subir estos archivos: ${failed.join(', ')}`);
        setFiles((prev) => prev.filter((f) => failed.includes(f.name)));
        setReply('');
      } else {
        setReply('');
        setFiles([]);
      }
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  const download = async (att) => {
    try {
      const res = await apiFetch(`/api/v1/software/customer/portal/tickets/${id}/attachments/${att.id}/download`, { headers: authHeaders() });
      if (res.status === 401) { logout(); return; }
      if (!res.ok) return;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = att.filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (_e) { /* noop */ }
  };

  if (loading) {
    return (
      <div>
        <div className="cp-skeleton cp-skeleton-title" />
        <div className="cp-skeleton cp-skeleton-card" style={{ height: 300 }} />
      </div>
    );
  }

  if (error && !ticket) {
    return (
      <div>
        {backLink}
        <EmptyState icon={BsHeadset} title="Ticket no disponible" subtitle={error} actionLabel="Reintentar" onAction={load} />
      </div>
    );
  }
  if (!ticket) return null;

  const attsFor = (messageId) => (ticket.attachments || []).filter((a) => a.message_id === messageId);
  const orphanAtts = (ticket.attachments || []).filter((a) => !a.message_id);
  const closed = ticket.status === 'cerrado';

  return (
    <div>
      {backLink}

      <div className="cp-card-static" style={{ padding: 20, marginBottom: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <h1 className="cp-page-title" style={{ margin: 0 }}>#{ticket.id} · {ticket.subject}</h1>
          <StatusBadge status={ticket.status} />
        </div>
        <div style={{ color: 'var(--cp-muted)', fontSize: '0.85rem', marginTop: 8 }}>
          Abierto el {formatDate(ticket.created_at)}
        </div>
        {orphanAtts.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: '0.8rem', color: 'var(--cp-muted)', marginBottom: 4 }}><BsPaperclip /> Adjuntos</div>
            {orphanAtts.map((a) => (
              <AttachmentRow key={a.id} att={a} onDownload={download} />
            ))}
          </div>
        )}
      </div>

      <div className="cp-card-static" style={{ padding: 18, marginBottom: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
        {(ticket.messages || []).map((m) => {
          if (m.sender_type === 'sistema') {
            return <div key={m.id} style={{ textAlign: 'center', color: 'var(--cp-muted)', fontSize: '0.8rem' }}>— {m.body} ({fmtDateTime(m.created_at)}) —</div>;
          }
          const mine = m.sender_type === 'cliente';
          return (
            <div key={m.id} style={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: '82%' }}>
              <div style={{ background: mine ? '#e7f4ff' : '#f1f3f5', borderRadius: 12, padding: '10px 14px', border: '1px solid rgba(0,0,0,.05)' }}>
                <div style={{ fontSize: '0.78rem', color: 'var(--cp-muted)', marginBottom: 4 }}>
                  <strong>{mine ? 'Tú' : (m.author_name || 'Soporte INTERCONECTA')}</strong> · {fmtDateTime(m.created_at)}
                </div>
                <div style={{ whiteSpace: 'pre-wrap', color: 'var(--cp-text)' }}>{m.body}</div>
                {attsFor(m.id).map((a) => (
                  <AttachmentRow key={a.id} att={a} onDownload={download} />
                ))}
              </div>
            </div>
          );
        })}
        {!(ticket.messages || []).length && <div style={{ textAlign: 'center', color: 'var(--cp-muted)' }}>Sin mensajes.</div>}
      </div>

      {error && <div className="cp-alert cp-alert-error" style={{ marginBottom: 12 }}>{error}</div>}

      {closed ? (
        <div className="cp-alert" style={{ background: '#f1f3f5', color: 'var(--cp-muted)' }}>Este ticket está cerrado. Si necesitas ayuda de nuevo, crea un nuevo ticket.</div>
      ) : (
        <div className="cp-card-static" style={{ padding: 16 }}>
          <textarea className="cp-input" rows="3" placeholder="Escribe tu respuesta…" value={reply} onChange={(e) => setReply(e.target.value)} style={{ marginBottom: 10 }} />
          {files.length > 0 && (
            <div style={{ marginBottom: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {files.map((f, i) => (
                <span key={i} className="cp-badge cp-badge-draft" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  {f.name}
                  <button type="button" onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'inherit' }}>×</button>
                </span>
              ))}
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <label className="cp-btn cp-btn-outline cp-btn-sm" style={{ cursor: 'pointer' }}>
              <BsPaperclip /> Adjuntar
              <input type="file" multiple hidden onChange={(e) => setFiles((prev) => [...prev, ...Array.from(e.target.files)])} />
            </label>
            <button className="cp-btn cp-btn-primary" onClick={send} disabled={sending || (!reply.trim() && !files.length)}>
              {sending ? 'Enviando…' : 'Responder'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const AttachmentRow = ({ att, onDownload }) => (
  <div style={{ marginTop: 6 }}>
    <button
      type="button"
      onClick={() => onDownload(att)}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: 'var(--cp-accent)', cursor: 'pointer', padding: 0, fontSize: '0.85rem' }}
    >
      <BsPaperclip /> {att.filename} <BsDownload />
    </button>
  </div>
);

export default CustomerSupportDetailPage;
